import React from 'react';

import { isValidRegExp } from '@neptune/shared/common-util';
import type {
  DirectMetricListSummary,
  ParsedDirectMetricList,
} from '@neptune/shared/widgets-domain';

import { AttributeDefinition, attributeDefinitionComparator } from 'domain/experiment/attribute';

type FloatSeriesSourcePickerSelectionParams = {
  setSelectedAttributes: React.Dispatch<React.SetStateAction<AttributeDefinition[]>>;
  setAttributePattern: (attributePattern?: string) => void;
};

export function createFloatSeriesAttributeDefinition(name: string): AttributeDefinition {
  return {
    name,
    type: 'floatSeries',
  };
}

export function parseDirectMetricList(text: string): ParsedDirectMetricList {
  const names = new Set<string>();
  let duplicatesRemoved = 0;

  for (const rawLine of text.split(/\r?\n/)) {
    const name = rawLine.trim();

    if (!name) {
      continue;
    }

    if (names.has(name)) {
      duplicatesRemoved += 1;
    } else {
      names.add(name);
    }
  }

  return { names, duplicatesRemoved };
}

export function isAttributeSelected(
  selectedAttributes: AttributeDefinition[],
  attribute: AttributeDefinition,
): boolean {
  return selectedAttributes.some((selectedAttribute) =>
    attributeDefinitionComparator(selectedAttribute, attribute),
  );
}

function createAttributeSelectionKeys(attributes: AttributeDefinition[]): Set<string> {
  return new Set(attributes.map(getAttributeSelectionKey));
}

function getAttributeSelectionKey(attribute: AttributeDefinition): string {
  return `${attribute.name}|${attribute.type}|${attribute.subproperty || 'auto'}`;
}

export function dedupeAttributeSelection(attributes: AttributeDefinition[]): AttributeDefinition[] {
  const seen = new Set<string>();
  const result: AttributeDefinition[] = [];

  for (const attribute of attributes) {
    const key = getAttributeSelectionKey(attribute);

    if (!seen.has(key)) {
      seen.add(key);
      result.push(attribute);
    }
  }

  return result;
}

export function addAttributesToSelection(
  selectedAttributes: AttributeDefinition[],
  attributesToAdd: AttributeDefinition[],
): AttributeDefinition[] {
  const seen = createAttributeSelectionKeys(selectedAttributes);
  let result = selectedAttributes;

  for (const attribute of attributesToAdd) {
    const key = getAttributeSelectionKey(attribute);

    if (!seen.has(key)) {
      seen.add(key);

      if (result === selectedAttributes) {
        result = [...selectedAttributes];
      }

      result.push(attribute);
    }
  }

  return result;
}

export function removeAttributeFromSelection(
  selectedAttributes: AttributeDefinition[],
  attributeToRemove: AttributeDefinition,
): AttributeDefinition[] {
  return selectedAttributes.filter(
    (selectedAttribute) => !attributeDefinitionComparator(selectedAttribute, attributeToRemove),
  );
}

export function toggleAttributeInSelection(
  selectedAttributes: AttributeDefinition[],
  attributeToToggle: AttributeDefinition,
): AttributeDefinition[] {
  if (isAttributeSelected(selectedAttributes, attributeToToggle)) {
    return removeAttributeFromSelection(selectedAttributes, attributeToToggle);
  }

  return [...selectedAttributes, attributeToToggle];
}

export function areSameAttributeSelection(
  left: AttributeDefinition[],
  right: AttributeDefinition[],
): boolean {
  const leftKeys = createAttributeSelectionKeys(left);
  const rightKeys = createAttributeSelectionKeys(right);

  if (leftKeys.size !== rightKeys.size) {
    return false;
  }

  for (const key of leftKeys) {
    if (!rightKeys.has(key)) {
      return false;
    }
  }

  return true;
}

function formatDirectMetricListHint({
  alreadySelectedCount,
  duplicateLinesCount,
  hasAny,
  toAddCount,
}: Pick<
  DirectMetricListSummary,
  'alreadySelectedCount' | 'duplicateLinesCount' | 'hasAny' | 'toAddCount'
>): string {
  if (!hasAny) {
    return '';
  }

  const parts: string[] = [];

  if (toAddCount > 0) {
    parts.push(`${toAddCount} metric${toAddCount === 1 ? '' : 's'} ready to add.`);

    if (alreadySelectedCount > 0) {
      parts.push(
        `Skipping ${alreadySelectedCount} already selected metric${
          alreadySelectedCount === 1 ? '' : 's'
        }.`,
      );
    }
  } else {
    parts.push('No new metrics to add.');

    if (alreadySelectedCount > 0) {
      parts.push(`All ${alreadySelectedCount} already selected.`);
    }
  }

  if (duplicateLinesCount > 0) {
    parts.push(
      `Ignoring ${duplicateLinesCount} duplicate line${duplicateLinesCount === 1 ? '' : 's'}.`,
    );
  }

  return parts.join(' ');
}

export function summarizeDirectMetricList(
  text: string,
  selectedAttributes: AttributeDefinition[],
): DirectMetricListSummary {
  const parsed = parseDirectMetricList(text);
  const selectedKeys = createAttributeSelectionKeys(selectedAttributes);
  const attributesToAdd: AttributeDefinition[] = [];
  let alreadySelectedCount = 0;

  for (const name of parsed.names) {
    const attribute = createFloatSeriesAttributeDefinition(name);

    if (selectedKeys.has(getAttributeSelectionKey(attribute))) {
      alreadySelectedCount += 1;
      continue;
    }

    attributesToAdd.push(attribute);
  }

  const hasAny = parsed.names.size > 0;
  const toAddCount = attributesToAdd.length;
  const duplicateLinesCount = parsed.duplicatesRemoved;

  const summary = {
    ...parsed,
    attributesToAdd,
    alreadySelectedCount,
    duplicateLinesCount,
    hasAny,
    toAddCount,
    canApply: toAddCount > 0,
  };

  return {
    ...summary,
    hint: formatDirectMetricListHint(summary),
  };
}

export function isValidAttributePattern(attributePattern: string | undefined): boolean {
  return (
    attributePattern === undefined || attributePattern === '' || isValidRegExp(attributePattern)
  );
}

export function normalizeAttributePattern(
  attributePattern: string | undefined,
): string | undefined {
  return attributePattern === '' ? undefined : attributePattern;
}

export function useFloatSeriesSourcePickerSelection({
  setSelectedAttributes,
  setAttributePattern,
}: FloatSeriesSourcePickerSelectionParams) {
  const addAttribute = React.useCallback(
    (attribute: AttributeDefinition) => {
      setSelectedAttributes((previousAttributes) =>
        addAttributesToSelection(previousAttributes, [attribute]),
      );
    },
    [setSelectedAttributes],
  );

  const addAttributes = React.useCallback(
    (attributes: AttributeDefinition[]) => {
      setSelectedAttributes((previousAttributes) =>
        addAttributesToSelection(previousAttributes, attributes),
      );
    },
    [setSelectedAttributes],
  );

  const removeAttribute = React.useCallback(
    (attribute: AttributeDefinition) => {
      setSelectedAttributes((previousAttributes) =>
        removeAttributeFromSelection(previousAttributes, attribute),
      );
    },
    [setSelectedAttributes],
  );

  const replaceAttributes = React.useCallback(
    (attributes: AttributeDefinition[]) => {
      setSelectedAttributes(dedupeAttributeSelection(attributes));
    },
    [setSelectedAttributes],
  );

  const clearAttributes = React.useCallback(() => {
    setSelectedAttributes([]);
  }, [setSelectedAttributes]);

  const toggleAttribute = React.useCallback(
    (attribute: AttributeDefinition) => {
      setSelectedAttributes((previousAttributes) =>
        toggleAttributeInSelection(previousAttributes, attribute),
      );
    },
    [setSelectedAttributes],
  );

  const setPattern = React.useCallback(
    (attributePattern?: string) => {
      setAttributePattern(normalizeAttributePattern(attributePattern));
    },
    [setAttributePattern],
  );

  return {
    addAttribute,
    addAttributes,
    clearAttributes,
    removeAttribute,
    replaceAttributes,
    setAttributePattern: setPattern,
    toggleAttribute,
  };
}
